import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Mic2, Sparkles, User, Sun, Moon, Star, Ticket } from 'lucide-react';

const ActingDrama: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "The Royal Stage Academy";
    
    return (
        <div className="min-h-screen bg-[#0a0a0f] font-sans text-stone-200 selection:bg-purple-900 selection:text-white">
            {/* Spotlight Effect (CSS) */}
            <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-purple-900/20 rounded-full blur-[120px] pointer-events-none z-0"></div>

            {/* Navbar */}
            <nav className="fixed w-full z-50 px-8 py-6 bg-gradient-to-b from-black/80 to-transparent flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <span className="font-serif text-2xl italic font-bold tracking-wider text-white">{schoolName}</span>
                </div>

                <div className="hidden md:flex items-center gap-8 text-xs font-bold uppercase tracking-[0.2em] text-stone-400">
                    <a href="#" className="hover:text-white hover:scale-105 transition-all">Auditions</a>
                    <a href="#" className="hover:text-white hover:scale-105 transition-all">Season</a>
                    <a href="#" className="hover:text-white hover:scale-105 transition-all">Alumni</a>
                    <a href="#" className="hover:text-white hover:scale-105 transition-all">Box Office</a>
                </div>

                <Button variant="outline" className="rounded-full border-purple-500/50 text-purple-400 hover:bg-purple-500 hover:text-white font-bold uppercase tracking-widest text-[10px] h-8 px-6 transition-all bg-transparent">
                    Apply
                </Button>
            </nav>

            {/* Hero Section */}
            <header className="relative min-h-screen flex items-center justify-center overflow-hidden">
                {/* Stage Curtains (Abstract) */}
                <div className="absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-black via-[#1a0525] to-transparent opacity-50"></div>
                <div className="absolute inset-y-0 right-0 w-1/4 bg-gradient-to-l from-black via-[#1a0525] to-transparent opacity-50"></div>

                <div className="relative z-10 text-center px-6 animate-in fade-in zoom-in duration-1000">
                    <div className="mb-6 flex justify-center text-purple-400/80">
                        <Sparkles size={24} />
                    </div>
                    
                    <h1 className="text-5xl md:text-8xl font-serif font-bold text-white mb-8 leading-tight drop-shadow-2xl">
                        Unmask Your <br/>
                        <span className="italic text-purple-400 font-light">True Self.</span>
                    </h1>
                    
                    <p className="font-sans text-stone-400 text-lg max-w-xl mx-auto mb-12 leading-relaxed">
                        A world-class conservatory for actors, directors, and playwrights. 
                        Where raw talent meets rigorous discipline.
                    </p>
                    
                    <div className="flex justify-center gap-6">
                        <Button className="bg-white text-black hover:bg-purple-200 font-bold uppercase tracking-widest text-xs h-12 px-8 rounded-full shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.5)] transition-all">
                            View Programs
                        </Button>
                        <Button variant="ghost" className="text-stone-300 hover:text-white font-bold uppercase tracking-widest text-xs h-12 px-8 flex items-center gap-2">
                             Watch Showreel <Ticket size={16} />
                        </Button>
                    </div>
                </div>

                {/* Bottom Gradient Fade */}
                <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#0a0a0f] to-transparent"></div>
            </header>

            {/* Methods Section */}
            <section className="py-24 px-8 relative z-10">
                <div className="max-w-5xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center md:text-left">
                        {[
                            { icon: User, title: "The Method", desc: "Stanislavski & Strasberg techniques for deep emotional truth." },
                            { icon: Mic2, title: "Voice & Speech", desc: "Projection, accents, and Shakespearean verse mastery." },
                            { icon: MaskIcon, title: "Movement", desc: "Physical theatre, stage combat, and Alexander Technique." },
                        ].map((item, i) => (
                            <div key={i} className="group p-8 border border-white/5 rounded-2xl hover:bg-white/5 transition-all hover:-translate-y-2 duration-500">
                                <div className="mb-6 w-12 h-12 bg-purple-900/30 rounded-full flex items-center justify-center text-purple-400 mx-auto md:mx-0 group-hover:scale-110 transition-transform">
                                    <item.icon size={24} />
                                </div>
                                <h3 className="text-2xl font-serif text-white mb-3 italic">{item.title}</h3>
                                <p className="text-stone-500 text-sm leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-black py-20 px-8 border-t border-white/5 text-center">
                 <div className="max-w-2xl mx-auto">
                     <span className="font-serif italic text-3xl text-white block mb-8">{schoolName}</span>
                     
                     <div className="flex justify-center gap-8 mb-12 text-purple-400">
                         <Sun size={20} />
                         <Star size={20} />
                         <Moon size={20} />
                     </div>
                     
                     <p className="font-sans text-xs text-stone-600 uppercase tracking-widest mb-8">
                         "All the world's a stage, and all the men and women merely players."
                     </p>
                     
                     <div className="text-[10px] text-stone-700">
                         © 2026 {schoolName}. All rights reserved.
                     </div>
                 </div>
            </footer>
        </div>
    );
};

// Lucide doesn't export 'Masks' directly sometimes, using a custom SVG wrapper or alternative if needed. 
// Actually 'Drama' might not be in the version, using a placeholder or creating a component.
// I used 'Masks' in the import but if it fails I'll swap to 'Smile' or similar. 
// Wait, Lucide has 'Theater' in newer versions? Let's check imports. 
// I'll stick to generic icons if needed. I see I imported 'Masks' but I'll use a safer icon if I'm not sure.
// Let's use 'User' or 'Smile'. I will use a custom icon for the mask concept if possible or just standard lucide. 
// Lucide has 'Drama' in recent, but I'll use a reliable one. 
// I'll define MaskIcon purely for safety.
const MaskIcon = ({ size, className }: { size: number, className?: string }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className={className}
    >
        <path d="M2 12a5 5 0 0 0 5 5 8 8 0 0 1 5 2 8 8 0 0 1 5-2 5 5 0 0 0 5-5V7h-5a8 8 0 0 0-5 2 8 8 0 0 0-5-2H2Z" />
        <path d="M6 11c1.5 0 3 .5 3 2-2 0-3 0-3-2Z" />
        <path d="M18 11c-1.5 0-3 .5-3 2 2 0 3 0 3-2Z" />
    </svg>
)

export default ActingDrama;
